import { useState } from 'react'
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/primitives'
import { cn } from '@/shared/ui/cn'

type TimePickerProps = {
  id?: string
  label?: string
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  required?: boolean
  error?: string
  ariaLabel?: string
  className?: string
}

type Period = 'AM' | 'PM'

const PERIODS: Period[] = ['AM', 'PM']

function pad(value: number): string {
  return value.toString().padStart(2, '0')
}

function parseTime(value: string): { hour24: number; minute: number } | null {
  if (!value) return null
  const [hourPart, minutePart] = value.split(':')
  const hour24 = Number.parseInt(hourPart, 10)
  const minute = Number.parseInt(minutePart, 10)
  if (Number.isNaN(hour24) || Number.isNaN(minute)) return null
  return { hour24, minute }
}

function toTwelveHour(hour24: number): { hour12: number; period: Period } {
  const period: Period = hour24 < 12 ? 'AM' : 'PM'
  const hour12 = ((hour24 + 11) % 12) + 1
  return { hour12, period }
}

function toTwentyFourHour(hour12: number, period: Period): number {
  const base = hour12 % 12
  return period === 'AM' ? base : base + 12
}

function formatTwelve(value: string): string {
  const parsed = parseTime(value)
  if (!parsed) return ''
  return `${toTwelveHour(parsed.hour24).hour12}:${pad(parsed.minute)}`
}

function periodOf(value: string): Period {
  const parsed = parseTime(value)
  return parsed ? toTwelveHour(parsed.hour24).period : 'AM'
}

function maskTime(raw: string): string {
  const cleaned = raw.replace(/[^\d:]/g, '')
  const colonIndex = cleaned.indexOf(':')

  if (colonIndex !== -1) {
    const hour = cleaned.slice(0, colonIndex).replace(/\D/g, '').slice(0, 2)
    if (hour === '') return ''
    const minute = cleaned
      .slice(colonIndex + 1)
      .replace(/\D/g, '')
      .slice(0, 2)
    return minute.length > 0 ? `${hour}:${minute}` : `${hour}:`
  }

  const digits = cleaned.slice(0, 4)
  if (digits.length === 0) return ''
  const twoDigitHour =
    digits[0] === '1' && (digits.length < 2 || digits[1] <= '2')
  const hourLength = twoDigitHour ? 2 : 1
  const hour = digits.slice(0, hourLength)
  const minute = digits.slice(hourLength, hourLength + 2)
  return minute.length > 0 ? `${hour}:${minute}` : hour
}

function parseTyped(text: string): { hour12: number; minute: number } | null {
  const match = text.trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!match) return null
  const hour12 = Number.parseInt(match[1], 10)
  const minute = Number.parseInt(match[2], 10)
  if (hour12 < 1 || hour12 > 12 || minute < 0 || minute > 59) return null
  return { hour12, minute }
}

function textToValue(text: string, period: Period): string {
  if (text.trim() === '') return ''
  const typed = parseTyped(text)
  if (!typed) return ''
  return `${pad(toTwentyFourHour(typed.hour12, period))}:${pad(typed.minute)}`
}

function TimePicker({
  id,
  label,
  value,
  onChange,
  disabled,
  required,
  error,
  ariaLabel,
  className,
}: TimePickerProps) {
  const [text, setText] = useState(() => formatTwelve(value))
  const [focused, setFocused] = useState(false)
  const period = periodOf(value)
  const fieldLabel = label ?? ariaLabel ?? 'Time'

  if (!focused && textToValue(text, period) !== value) {
    setText(formatTwelve(value))
  }

  const commit = (nextText: string, nextPeriod: Period) => {
    if (nextText.trim() === '') {
      onChange('')
      return
    }
    const next = textToValue(nextText, nextPeriod)
    if (next === '') return
    onChange(next)
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label ? (
        <Label htmlFor={id}>
          {label}
          {required ? ' *' : null}
        </Label>
      ) : null}
      <div className="flex min-w-0 items-center gap-2">
        <Input
          id={id}
          type="text"
          inputMode="numeric"
          maxLength={5}
          placeholder="h:mm"
          autoComplete="off"
          aria-label={label ? undefined : fieldLabel}
          aria-invalid={error ? true : undefined}
          aria-describedby={error && id ? `${id}-error` : undefined}
          value={text}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => {
            const masked = maskTime(e.target.value)
            setText(masked)
            commit(masked, period)
          }}
          className="flex-1"
        />
        <Select
          value={period}
          disabled={disabled}
          onValueChange={(next) => commit(text, next as Period)}
        >
          <SelectTrigger
            aria-label={`${fieldLabel} AM/PM`}
            aria-invalid={error ? true : undefined}
            className="w-24 bg-card"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {error ? (
        <p
          id={id ? `${id}-error` : undefined}
          role="alert"
          className="text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}
    </div>
  )
}

export { TimePicker }
export type { TimePickerProps }
